import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import QuickLookupPalette from '../components/QuickLookupPalette'
import { useAuthStore } from '../stores/authStore'
import { storeSharePayload, consumeSharePayload } from '../utils/shareToken'
import { sanitizeShareText } from '../utils/sanitizeShareText'
import { readAndClearShareQueue } from '../utils/shareQueue'

/** Phrases at or under this word count go to Quick Lookup; longer text goes to Text Import. */
const WORD_THRESHOLD = 5

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

export default function ShareTargetPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [ready, setReady] = useState(false)
  const [lookupText, setLookupText] = useState('')

  useEffect(() => {
    let active = true

    async function resolve() {
      // POST share target: SW writes text to IDB then 303-redirects here (no query params).
      // GET legacy fallback: text arrives in ?text= or ?url= search params.
      const idbText = await readAndClearShareQueue()
      const paramText = (searchParams.get('text') ?? searchParams.get('url') ?? '').trim()
      const rawInput = (idbText || paramText).trim()

      // Read auth state imperatively — we only need it once at mount.
      const { isAuthenticated, user } = useAuthStore.getState()

      if (rawInput) {
        // Store before any redirect so the token survives login and onboarding navigation.
        await storeSharePayload(rawInput)

        if (!isAuthenticated) {
          navigate('/login?returnTo=/share-target', { replace: true })
          return
        }

        // Authenticated but onboarding not yet completed — preserve through onboarding.
        if (user && !user.onboarding_completed) {
          navigate('/onboarding?returnTo=/share-target', { replace: true })
          return
        }

        if (!active) return

        const { query, rawDecoded } = sanitizeShareText(rawInput)

        if (countWords(rawDecoded) > WORD_THRESHOLD) {
          navigate('/text-import', { replace: true, state: { prefillText: rawDecoded } })
          return
        }

        setLookupText(query)
        setReady(true)
      } else {
        // Post-login / post-onboarding path: verify the signed payload stored before redirect.
        const text = await consumeSharePayload()
        if (!active) return

        if (!text) {
          // No valid token — tampered URL or expired/missing payload.
          navigate('/home', { replace: true })
          return
        }

        const { query, rawDecoded } = sanitizeShareText(text)

        if (countWords(rawDecoded) > WORD_THRESHOLD) {
          navigate('/text-import', { replace: true, state: { prefillText: rawDecoded } })
          return
        }

        setLookupText(query)
        setReady(true)
      }
    }

    resolve()
    return () => { active = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)' }}>
      {!ready && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}>
          <span style={{
            color: 'var(--text-secondary)',
            fontSize: 14,
            fontFamily: 'var(--font-ui)',
          }}>
            Loading…
          </span>
        </div>
      )}

      {ready && (
        <>
          {/* Minimal brand context visible behind the bottom sheet */}
          <div style={{
            padding: '20px 20px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-ui)',
              letterSpacing: '-0.02em',
            }}>
              CardSmith
            </span>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
              · Shared word
            </span>
          </div>

          <QuickLookupPalette
            open
            onClose={() => navigate('/home', { replace: true })}
            layout="sheet"
            initialQuery={lookupText}
          />
        </>
      )}
    </div>
  )
}
